import React, { useState, useEffect } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { useTranslation } from 'react-i18next';
import { Search, CreditCard, Users, CheckCircle, AlertCircle, X } from 'lucide-react';

const Payment = () => {
  const { api, user } = useAuth();
  const { t, i18n } = useTranslation('payment');
  const isRTL = i18n.language === 'ar';
  
  // State management
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedStream, setSelectedStream] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: '', content: '' });
  
  // Payment data
  const [payments, setPayments] = useState({});
  
  // Payment method options
  const paymentMethods = [
    { value: 'Cash', label: t('paymentMethods.cash') },
    { value: 'Bank Transfer', label: t('paymentMethods.bankTransfer') },
    { value: 'Mpesa', label: t('paymentMethods.mpesa') },
    { value: 'Cheque', label: t('paymentMethods.cheque') },
    { value: 'Mobile Money', label: t('paymentMethods.mobileMoney') }
  ];
  
  // Clear message after 5 seconds
  useEffect(() => {
    if (message.content) {
      const timer = setTimeout(() => {
        setMessage({ type: '', content: '' });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  // Fetch classes
  useEffect(() => {
    const fetchClasses = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/api/v1/admin/schools/${user.school_reg}/classes`);
        setClasses(response.data);
      } catch (error) {
        setMessage({
          type: 'error',
          content: t('messages.failedToLoadClasses')
        });
      } finally {
        setLoading(false);
      }
    };

    if (user?.school_reg) {
      fetchClasses();
    }
  }, [api, user?.school_reg, t]);

  // Fetch students when filters change
  useEffect(() => {
    if (selectedClass && selectedStream) {
      fetchStudents(1);
    } else {
      setStudents([]);
      setPayments({});
    }
  }, [selectedClass, selectedStream, searchTerm]);

  const fetchStudents = async (page = 1) => {
    try {
      setStudentsLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: '50',
        class_id: selectedClass,
        stream_id: selectedStream,
      });
      
      if (searchTerm) {
        params.append('search', searchTerm);
      }

      const response = await api.get(`/api/v1/admin/schools/${user.school_reg}/students?${params}`);
      
      if (page === 1) {
        setStudents(response.data.items);
      } else {
        setStudents(prev => [...prev, ...response.data.items]);
      }
      
      setTotalPages(response.data.total_pages);
      setCurrentPage(page);
    } catch (error) {
      setMessage({
        type: 'error',
        content: t('messages.failedToLoadStudents')
      });
    } finally {
      setStudentsLoading(false);
    }
  };

  const loadMoreStudents = () => {
    if (currentPage < totalPages) {
      fetchStudents(currentPage + 1);
    }
  };

  const handlePaymentChange = (studentId, field, value) => {
    setPayments(prev => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        [field]: value
      }
    }));
  };

  const getSelectedClassName = () => {
    const classObj = classes.find(c => c.id.toString() === selectedClass);
    const stream = classObj?.streams.find(s => s.id.toString() === selectedStream);
    return stream ? `${classObj.name} - ${stream.name}` : '';
  };

  const validatePayments = () => {
    // Only include students who have required fields filled (amount and payment_method)
    const studentsWithCompletePayments = Object.keys(payments).filter(studentId => {
      const payment = payments[studentId];
      return payment && 
             payment.amount > 0 && 
             payment.payment_method?.trim();
    });

    const errors = [];
    
    // Check for students with partial data (to warn about incomplete entries)
    const studentsWithPartialData = Object.keys(payments).filter(studentId => {
      const payment = payments[studentId];
      if (!payment) return false;
      
      const hasAmount = payment.amount > 0;
      const hasMethod = payment.payment_method?.trim();
      
      // Has some data but not all required fields
      return (hasAmount || hasMethod) && !(hasAmount && hasMethod);
    });

    // Add warnings for incomplete entries (but don't block submission)
    studentsWithPartialData.forEach(studentId => {
      const payment = payments[studentId];
      const studentName = students.find(s => s.id.toString() === studentId)?.name;
      
      if (!payment.amount || payment.amount <= 0) {
        errors.push(t('messages.amountMissingForStudent', { name: studentName }));
      }
      if (!payment.payment_method?.trim()) {
        errors.push(t('messages.paymentMethodMissingForStudent', { name: studentName }));
      }
    });

    return { 
      valid: studentsWithCompletePayments.length > 0, 
      errors, 
      studentsWithPayments: studentsWithCompletePayments,
      hasIncompleteEntries: studentsWithPartialData.length > 0
    };
  };

  const handleSubmitPayments = async () => {
    const validation = validatePayments();
    
    if (!validation.valid) {
      setMessage({
        type: 'error',
        content: t('messages.addCompletePaymentDetails')
      });
      return;
    }

    // Show warning if there are incomplete entries, but allow submission
    if (validation.hasIncompleteEntries && validation.errors.length > 0) {
      console.warn('Some entries are incomplete and will be skipped:', validation.errors);
    }

    try {
      setSubmitting(true);
      const paymentPromises = validation.studentsWithPayments.map(studentId => {
        const payment = payments[studentId];
        const payload = {
          student_id: parseInt(studentId),
          amount: parseFloat(payment.amount),
          payment_method: payment.payment_method.trim(),
          payment_date: new Date().toISOString()
        };

        // Only add transaction_reference if it's provided (it's optional)
        if (payment.transaction_reference?.trim()) {
          payload.transaction_reference = payment.transaction_reference.trim();
        }

        return api.post('/api/v1/payment/', payload);
      });

      await Promise.all(paymentPromises);
      
      const successMessage = t('messages.successfullyProcessedPayments', { 
        count: validation.studentsWithPayments.length 
      });
      const warningMessage = validation.hasIncompleteEntries ? 
        t('messages.incompleteEntriesSkipped', { 
          count: Object.keys(payments).length - validation.studentsWithPayments.length 
        }) : '';
      
      setMessage({
        type: 'success',
        content: successMessage + warningMessage
      });
      
      // Clear payment data
      setPayments({});
      
    } catch (error) {
      let errorMessage = t('messages.failedToProcessPayments');
      
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          errorMessage = error.response.data.detail.map(err => err.msg).join(', ');
        } else {
          errorMessage = error.response.data.detail;
        }
      }
      
      setMessage({
        type: 'error',
        content: errorMessage
      });
    } finally {
      setSubmitting(false);
    }
  };

  const filteredStudents = students.filter(student =>
    student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.admission_number.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Count students with complete payment data
  const studentsWithCompletePayments = Object.keys(payments).filter(studentId => {
    const payment = payments[studentId];
    return payment && 
           payment.amount > 0 && 
           payment.payment_method?.trim();
  }).length;

  return (
    <div className={`max-w-7xl mx-auto bg-gray-50 min-h-screen ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <div className={`flex items-center gap-3 mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <h1 className="text-2xl font-bold text-gray-800">{t('title')}</h1>
        </div>
        
        {/* Message Display */}
        {message.content && (
          <div className={`mb-4 p-4 rounded-lg flex items-center gap-3 ${
            message.type === 'success' 
              ? 'bg-green-50 text-green-800 border border-green-200' 
              : 'bg-red-50 text-red-800 border border-red-200'
          } ${isRTL ? 'flex-row-reverse' : ''}`}>
            {message.type === 'success' ? (
              <CheckCircle size={20} />
            ) : (
              <AlertCircle size={20} />
            )}
            <span className="flex-1">{message.content}</span>
            <button 
              onClick={() => setMessage({ type: '', content: '' })}
              className="text-gray-500 hover:text-gray-700"
            >
              <X size={16} />
            </button>
          </div>
        )}

        {/* Selection Filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Class */}
          <div>
            <label className={`block text-sm font-medium text-gray-700 mb-2 ${isRTL ? 'text-right' : ''}`}>
              <Users size={16} className={`inline ${isRTL ? 'ml-1' : 'mr-1'}`} />
              {t('class')}
            </label>
            <select
              value={selectedClass}
              onChange={(e) => {
                setSelectedClass(e.target.value);
                setSelectedStream('');
              }}
              className={`w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0E5252] focus:border-transparent ${
                isRTL ? 'text-right' : ''
              }`}
              disabled={loading}
            >
              <option value="">{t('selectClass')}</option>
              {classes.map(classItem => (
                <option key={classItem.id} value={classItem.id}>
                  {classItem.name}
                </option>
              ))}
            </select>
          </div>

          {/* Stream */}
          <div>
            <label className={`block text-sm font-medium text-gray-700 mb-2 ${isRTL ? 'text-right' : ''}`}>
              {t('stream')}
            </label>
            <select
              value={selectedStream}
              onChange={(e) => setSelectedStream(e.target.value)}
              className={`w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0E5252] focus:border-transparent ${
                isRTL ? 'text-right' : ''
              }`}
              disabled={!selectedClass}
            >
              <option value="">{t('selectStream')}</option>
              {classes
                .find(c => c.id.toString() === selectedClass)
                ?.streams.map(stream => (
                  <option key={stream.id} value={stream.id}>
                    {stream.name}
                  </option>
                ))
              }
            </select>
          </div>
        </div>
      </div>

      {/* Students List */}
      {selectedClass && selectedStream && (
        <div className="bg-white rounded-lg shadow-sm">
          {/* Students Header */}
          <div className="p-6 border-b border-gray-200">
            <div className={`flex flex-col md:flex-row md:items-center justify-between gap-4 ${
              isRTL ? 'md:flex-row-reverse' : ''
            }`}>
              <div className={isRTL ? 'text-right' : ''}>
                <h2 className="text-lg font-semibold text-gray-800">
                  {t('student')} - {getSelectedClassName()}
                </h2>
              </div>
              
              {/* Search */}
              <div className="relative">
                <Search 
                  className={`absolute top-1/2 transform -translate-y-1/2 text-gray-400 ${
                    isRTL ? 'right-3' : 'left-3'
                  }`} 
                  size={20} 
                />
                <input
                  type="text"
                  placeholder={t('searchStudents')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={`py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0E5252] focus:border-transparent w-full md:w-64 ${
                    isRTL ? 'pr-10 pl-4 text-right' : 'pl-10 pr-4'
                  }`}
                />
              </div>
            </div>
          </div>

          {/* Students Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${
                    isRTL ? 'text-right' : 'text-left'
                  }`}>
                    {t('admissionNo')}
                  </th>
                  <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${
                    isRTL ? 'text-right' : 'text-left'
                  }`}>
                    {t('student')}
                  </th>
                  <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${
                    isRTL ? 'text-right' : 'text-left'
                  }`}>
                    {t('amount')} *
                  </th>
                  <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${
                    isRTL ? 'text-right' : 'text-left'
                  }`}>
                    {t('paymentMethod')} *
                  </th>
                  <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${
                    isRTL ? 'text-right' : 'text-left'
                  }`}>
                    {t('transactionReference')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {studentsLoading && students.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                      {t('loadingStudents')}
                    </td>
                  </tr>
                ) : filteredStudents.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                      {t('noStudentsFound')}
                    </td>
                  </tr>
                ) : (
                  filteredStudents.map((student) => (
                    <tr key={student.id} className="hover:bg-gray-50">
                      <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-900 ${
                        isRTL ? 'text-right' : ''
                      }`}>
                        {student.admission_number}
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap ${isRTL ? 'text-right' : ''}`}>
                        <div className="text-sm font-medium text-gray-900">{student.name}</div>
                        <div className="text-sm text-gray-500">{student.gender}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="number"
                          placeholder={t('placeholders.amount')}
                          min="0"
                          step="0.01"
                          value={payments[student.id]?.amount || ''}
                          onChange={(e) => handlePaymentChange(student.id, 'amount', e.target.value)}
                          className={`w-24 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#0E5252] focus:border-transparent text-sm ${
                            isRTL ? 'text-right' : ''
                          }`}
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <select
                          value={payments[student.id]?.payment_method || ''}
                          onChange={(e) => handlePaymentChange(student.id, 'payment_method', e.target.value)}
                          className={`w-32 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#0E5252] focus:border-transparent text-sm ${
                            isRTL ? 'text-right' : ''
                          }`}
                        >
                          <option value="">{t('select')}</option>
                          {paymentMethods.map(method => (
                            <option key={method.value} value={method.value}>
                              {method.label}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="text"
                          placeholder={t('enterReference')}
                          value={payments[student.id]?.transaction_reference || ''}
                          onChange={(e) => handlePaymentChange(student.id, 'transaction_reference', e.target.value)}
                          className={`w-40 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#0E5252] focus:border-transparent text-sm ${
                            isRTL ? 'text-right' : ''
                          }`}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Load More & Submit */}
          <div className={`p-6 border-t border-gray-200 flex flex-col md:flex-row justify-between items-center gap-4 ${
            isRTL ? 'md:flex-row-reverse' : ''
          }`}>
            <div className={`flex items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
              {currentPage < totalPages && (
                <button
                  onClick={loadMoreStudents}
                  disabled={studentsLoading}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 text-sm"
                >
                  {studentsLoading ? t('processing') : `${t('loadMore')} (${students.length} of ${totalPages * 50})`}
                </button>
              )}
              <span className="text-sm text-gray-600">
                {studentsWithCompletePayments} {t('studentsReadyForPayment')}
              </span>
            </div>
            
            <button
              onClick={handleSubmitPayments}
              disabled={submitting || studentsWithCompletePayments === 0}
              className={`px-6 py-3 bg-[#0E5252] text-white rounded-lg hover:bg-[#0E5252]/90 disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center gap-2 ${
                isRTL ? 'flex-row-reverse' : ''
              }`}
            >
              {submitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  {t('processing')}
                </>
              ) : (
                <>
                  <CreditCard size={16} />
                  {t('submitPayments')}
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Payment;